<script setup>
import { useAlert } from 'dashboard/composables';
import AddAutomationRule from './AddAutomationRule.vue';
import EditAutomationRule from './EditAutomationRule.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';
import AutomationRulesTable from './AutomationRulesTable.vue';
import AutomationFolderDialog from './AutomationFolderDialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const confirmDialog = ref(null);
const folderDialogRef = ref(null);

const loading = ref({});
const savingFolder = ref(false);
const addDialogRef = ref(null);
const editDialogRef = ref(null);
const showDeleteConfirmationPopup = ref(false);
const showDeleteFolderPopup = ref(false);
const selectedAutomation = ref({});
const selectedTab = ref('all');
const selectedFolder = ref(null);
const editingFolder = ref(null);
const searchQuery = ref('');
const toggleModalTitle = ref(t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION')
);

const records = computed(() => getters['automations/getAutomations'].value);
const folders = computed(() => getters['automations/getFolders'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});

const ungroupedCount = computed(
  () => records.value.filter(rule => !rule.automation_folder_id).length
);

const folderCounts = computed(() => {
  const counts = {};
  records.value.forEach(rule => {
    if (!rule.automation_folder_id) return;
    counts[rule.automation_folder_id] =
      (counts[rule.automation_folder_id] || 0) + 1;
  });
  return counts;
});

const shortFolderName = name =>
  name.length > 20 ? `${name.slice(0, 20)}...` : name;

const tabs = computed(() => {
  const items = [
    {
      key: 'all',
      name: t('AUTOMATION.FOLDERS.ALL'),
      title: '',
      count: records.value.length,
    },
    ...folders.value.map(folder => ({
      key: folder.id,
      name: shortFolderName(folder.name),
      title: folder.name,
      count: folderCounts.value[folder.id] || 0,
    })),
  ];
  if (ungroupedCount.value > 0) {
    items.push({
      key: 'none',
      name: t('AUTOMATION.FOLDERS.UNGROUPED'),
      title: '',
      count: ungroupedCount.value,
    });
  }
  return items;
});

const activeTabIndex = computed(() => {
  const index = tabs.value.findIndex(tab => tab.key === selectedTab.value);
  return index === -1 ? 0 : index;
});

const activeFolder = computed(
  () => folders.value.find(folder => folder.id === selectedTab.value) || null
);

const visibleRecords = computed(() => {
  if (!folders.value.length || selectedTab.value === 'all') {
    return filteredRecords.value;
  }
  if (selectedTab.value === 'none') {
    return filteredRecords.value.filter(rule => !rule.automation_folder_id);
  }
  return filteredRecords.value.filter(
    rule => rule.automation_folder_id === selectedTab.value
  );
});

const noDataMessage = computed(() => {
  if (searchQuery.value.trim()) return t('AUTOMATION.NO_RESULTS');
  if (activeFolder.value || selectedTab.value === 'none') {
    return t('AUTOMATION.FOLDERS.EMPTY');
  }
  return t('AUTOMATION.LIST.404');
});

watch([folders, records], () => {
  if (selectedTab.value === 'all') return;
  if (selectedTab.value === 'none') {
    if (!ungroupedCount.value) selectedTab.value = 'all';
    return;
  }
  if (!folders.value.some(folder => folder.id === selectedTab.value)) {
    selectedTab.value = 'all';
  }
});

const onTabChange = index => {
  selectedTab.value = tabs.value[index]?.key ?? 'all';
};
const uiFlags = computed(() => getters['automations/getUIFlags'].value);
const accountId = computed(() => getters.getCurrentAccountId.value);

const deleteConfirmText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.YES')} ${selectedAutomation.value.name}`
);

const deleteRejectText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.NO')} ${selectedAutomation.value.name}`
);

const deleteMessage = computed(() => ` ${selectedAutomation.value.name}?`);

const isSLAEnabled = computed(() =>
  getters['accounts/isFeatureEnabledonAccount'].value(accountId.value, 'sla')
);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('contacts/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get');
  store.dispatch('automations/get');
  store.dispatch('automations/getFolders');
  if (isSLAEnabled.value) {
    store.dispatch('sla/get');
  }
});

const openAddPopup = () => {
  addDialogRef.value?.open();
};
const hideAddPopup = () => {
  addDialogRef.value?.close();
};

const openEditPopup = response => {
  selectedAutomation.value = { ...response };
  editDialogRef.value?.open();
};
const hideEditPopup = () => {
  editDialogRef.value?.close();
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedAutomation.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteAutomation = async id => {
  try {
    await store.dispatch('automations/delete', id);
    useAlert(t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};
const confirmDeletion = () => {
  loading.value[selectedAutomation.value.id] = true;
  closeDeletePopup();
  deleteAutomation(selectedAutomation.value.id);
};
const cloneAutomation = async ({ id }) => {
  try {
    await store.dispatch('automations/clone', id);
    useAlert(t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
    store.dispatch('automations/get');
  } catch (error) {
    useAlert(t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[selectedAutomation.value.id] = false;
  }
};

const submitAutomation = async (payload, mode) => {
  try {
    const action =
      mode === 'edit' ? 'automations/update' : 'automations/create';
    const successMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.SUCCESS_MESSAGE')
        : t('AUTOMATION.ADD.API.SUCCESS_MESSAGE');
    await store.dispatch(action, payload);
    useAlert(successMessage);
    hideAddPopup();
    hideEditPopup();
  } catch (error) {
    const errorMessage =
      mode === 'edit'
        ? t('AUTOMATION.EDIT.API.ERROR_MESSAGE')
        : t('AUTOMATION.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
const toggleAutomation = async ({ id, name, status }) => {
  try {
    if (status) {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.DEACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    } else {
      toggleModalTitle.value = t('AUTOMATION.TOGGLE.ACTIVATION_TITLE');
      toggleModalDescription.value = t(
        'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION',
        {
          automationName: name,
        }
      );
    }

    const ok = await confirmDialog.value.showConfirmation();
    if (ok) {
      await store.dispatch('automations/update', {
        id: id,
        active: !status,
      });
      const message = status
        ? t('AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL')
        : t('AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL');
      useAlert(message);
    }
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const tableHeaders = computed(() => {
  return [
    t('AUTOMATION.LIST.TABLE_HEADER.NAME'),
    t('AUTOMATION.LIST.TABLE_HEADER.ACTIVE'),
    t('AUTOMATION.LIST.TABLE_HEADER.CREATED_ON'),
    t('AUTOMATION.LIST.TABLE_HEADER.ACTIONS'),
  ];
});

const openCreateFolder = () => {
  editingFolder.value = null;
  folderDialogRef.value?.open('');
};

const openRenameFolder = folder => {
  editingFolder.value = folder;
  folderDialogRef.value?.open(folder.name);
};

const saveFolder = async name => {
  savingFolder.value = true;
  try {
    if (editingFolder.value) {
      await store.dispatch('automations/updateFolder', {
        id: editingFolder.value.id,
        name,
      });
      useAlert(t('AUTOMATION.FOLDERS.API.UPDATE_SUCCESS'));
    } else {
      const created = await store.dispatch('automations/createFolder', {
        name,
      });
      if (created?.id) selectedTab.value = created.id;
      useAlert(t('AUTOMATION.FOLDERS.API.CREATE_SUCCESS'));
    }
    folderDialogRef.value?.close();
  } catch (error) {
    useAlert(
      editingFolder.value
        ? t('AUTOMATION.FOLDERS.API.UPDATE_ERROR')
        : t('AUTOMATION.FOLDERS.API.CREATE_ERROR')
    );
  } finally {
    savingFolder.value = false;
  }
};

const openDeleteFolder = folder => {
  selectedFolder.value = folder;
  showDeleteFolderPopup.value = true;
};

const closeDeleteFolder = () => {
  showDeleteFolderPopup.value = false;
};

const confirmDeleteFolder = async () => {
  const folder = selectedFolder.value;
  closeDeleteFolder();
  if (!folder) return;

  try {
    await store.dispatch('automations/deleteFolder', folder.id);
    if (selectedTab.value === folder.id) selectedTab.value = 'all';
    useAlert(t('AUTOMATION.FOLDERS.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('AUTOMATION.FOLDERS.API.DELETE_ERROR'));
  }
};

const moveAutomation = async ({ id, automation_folder_id: folderId }) => {
  try {
    await store.dispatch('automations/update', {
      id,
      automation_folder_id: folderId,
    });
    useAlert(t('AUTOMATION.FOLDERS.API.MOVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AUTOMATION.FOLDERS.API.MOVE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION.LOADING')"
    :no-records-found="!records.length && !folders.length"
    :no-records-message="$t('AUTOMATION.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AUTOMATION.HEADER')"
        :description="$t('AUTOMATION.DESCRIPTION')"
        :link-text="$t('AUTOMATION.LEARN_MORE')"
        :search-placeholder="$t('AUTOMATION.SEARCH_PLACEHOLDER')"
        feature-name="automation"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AUTOMATION.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center gap-2">
            <Button
              :label="$t('AUTOMATION.FOLDERS.ADD')"
              color="slate"
              variant="faded"
              size="sm"
              @click="openCreateFolder"
            />
            <Button
              :label="$t('AUTOMATION.HEADER_BTN_TXT')"
              size="sm"
              @click="openAddPopup"
            />
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div v-if="folders.length" class="flex items-center gap-2 min-w-0 mb-2">
        <woot-tabs
          :index="activeTabIndex"
          class="min-w-0 flex-1 [&_ul]:px-0"
          @change="onTabChange"
        >
          <woot-tabs-item
            v-for="(tab, index) in tabs"
            :key="tab.key"
            :index="index"
            :name="tab.name"
            :title="tab.title"
            :count="tab.count"
            is-compact
          />
        </woot-tabs>
        <div v-if="activeFolder" class="flex gap-1 flex-shrink-0">
          <Button
            v-tooltip.top="$t('AUTOMATION.FOLDERS.EDIT')"
            icon="i-lucide-pencil"
            slate
            ghost
            sm
            @click="openRenameFolder(activeFolder)"
          />
          <Button
            v-tooltip.top="$t('AUTOMATION.FOLDERS.DELETE')"
            icon="i-lucide-trash"
            slate
            ghost
            sm
            @click="openDeleteFolder(activeFolder)"
          />
        </div>
      </div>
      <AutomationRulesTable
        :headers="tableHeaders"
        :items="visibleRecords"
        :folders="folders"
        :loading="loading"
        :no-data-message="noDataMessage"
        @clone="cloneAutomation"
        @toggle="toggleAutomation"
        @edit="openEditPopup"
        @delete="openDeletePopup"
        @move="moveAutomation"
      />
    </template>

    <AddAutomationRule ref="addDialogRef" @save-automation="submitAutomation" />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <EditAutomationRule
      ref="editDialogRef"
      :selected-response="selectedAutomation"
      @save-automation="submitAutomation"
    />
    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />
    <AutomationFolderDialog
      ref="folderDialogRef"
      :mode="editingFolder ? 'rename' : 'create'"
      :is-loading="savingFolder"
      @save="saveFolder"
    />
    <woot-delete-modal
      v-model:show="showDeleteFolderPopup"
      :on-close="closeDeleteFolder"
      :on-confirm="confirmDeleteFolder"
      :title="$t('AUTOMATION.FOLDERS.DELETE_TITLE')"
      :message="$t('AUTOMATION.FOLDERS.DELETE_MESSAGE')"
      :message-value="selectedFolder?.name"
      :confirm-text="$t('AUTOMATION.FOLDERS.DELETE_CONFIRM')"
      :reject-text="$t('AUTOMATION.FOLDERS.CANCEL')"
    />
  </SettingsLayout>
</template>
